/**
 * QA Panel — Jackpot Widget injector (lobby side).
 *
 * Mounts the **EGT Digital Jackpot Widgets** — the separate, operator-facing
 * `jackpot-widget` product — as a section on the lobby page, so QA can look at
 * them in the real layout, on the real device, without the lobby ever shipping
 * them.
 *
 * This is NOT an integration. The lobby renders its own jackpots from its own
 * store (`features/jackpots`, polling `/api/jackpot/stats/internal`); the widget
 * bundle is a foreign artifact with its own store polling `/api/jackpot/stats`.
 * Both run side by side while the section is up, at different intervals, so
 * their numbers will differ slightly. That is expected, not a bug.
 *
 * The whole feature is dead-stripped in prod by the qaPanel gate in
 * LobbyApp.tsx, which is what keeps a QA-only bundle out of players' hands.
 *
 * ── How the bundle gets here ────────────────────────────────────────────────
 * Testers have only two things: the widget **zip** and the lobby. So they pick
 * the zip file in the panel and everything happens in the browser — no host to
 * deploy to, no tooling from the widget project. `zipInjector.ts` unpacks it,
 * loads the bundle from blob URLs, and translates the widget's asset URLs to
 * in-memory blobs. What QA sees is the exact artifact from the zip.
 *
 * ── Why a MutationObserver on the section ───────────────────────────────────
 * The section is a plain DOM node inserted into `[data-section-container]`
 * (MultiViewLobby's anchor — an attribute, because the container's class is a
 * hashed CSS-module name). It is outside React, so React does not manage it and
 * does not preserve it either: any `setSections` push (preset import, Reset to
 * API, a settings refetch) re-renders the container and drops our node. The
 * observer notices and puts it back.
 *
 * ── The teardown caveat ─────────────────────────────────────────────────────
 * The widget bundle has no dispose API — its factory calls
 * `createRoot(el).render()` and keeps the root. Removing our node therefore does
 * NOT unmount the widgets: their subscriptions are never released and the widget
 * store keeps polling for the rest of the page's life. "Disable" releases what
 * we own (DOM hooks, injected styles, blob URLs) but is honest that the widgets
 * stay live — the tab tells QA to reload for a full reset.
 */

import i18next from 'i18next';
import { useAppStore } from '../../../store/app/appStore';
import type { WidgetInjector } from './widgetInjector';

const LS_KEY = 'qa-panel-jackpot-widget';

/** Where MultiViewLobby marks the section list. Attribute, not a hashed class. */
const SECTION_ANCHOR = '[data-section-container]';

/** id of the style that neutralises the lobby cascade inside our section. */
const ENV_FIX_STYLE_ID = 'egtd-qa-jpw-env-fix';

export type JackpotWidgetLoadStatus = 'idle' | 'loading' | 'ready' | 'error';

/** The mount options QA can actually drive. No baseUrl — the source is a zip. */
export interface JackpotWidgetConfig {
    /** Empty means "every jackpot the bundle registers". */
    jackpots: string[];
    type: string;
    currency: string;
    units: number;
    language: string;
    /** Serve canned stats in-page instead of calling the API. */
    mock: boolean;
    /** Stats API origin. Empty falls back to the lobby's own apiHost. */
    apiHost: string;
}

export interface JackpotWidgetState {
    enabled: boolean;
    config: JackpotWidgetConfig;
    status: JackpotWidgetLoadStatus;
    error: string | null;
    /** Name of the loaded zip, for display. Null until one is picked. */
    zipName: string | null;
    /** Version of the loaded bundle, once a zip has been unpacked. */
    version: string | null;
    /** True once a zip has been unpacked and its bundle is ready to mount. */
    loaded: boolean;
    /**
     * Lists read from the loaded bundle, so the popup's dropdowns cannot drift
     * from what it actually registers. Null until a zip is loaded.
     */
    available: { jackpots: string[]; types: string[]; languages: string[] } | null;
    /** True once a section has been mounted in this page's lifetime. */
    everMounted: boolean;
}

interface PersistedShape {
    config?: unknown;
}

/**
 * The stored config as it comes off localStorage — every field `unknown`,
 * because it may have been written by an older build (or hand-edited). The zip
 * bytes are never persisted (too large, and testers have the file), so only the
 * form fields round-trip.
 */
interface StoredConfig {
    jackpots?: unknown;
    type?: unknown;
    currency?: unknown;
    units?: unknown;
    language?: unknown;
    mock?: unknown;
    apiHost?: unknown;
}

const defaultConfig = (): JackpotWidgetConfig => ({
    jackpots: [],
    type: 'standard',
    // Filled from the lobby at init(); a hardcoded default here would show QA
    // a currency the player is not actually in.
    currency: '',
    units: 100,
    language: '',
    mock: false,
    apiHost: '',
});

export class JackpotWidgetController {
    private state: JackpotWidgetState = {
        enabled: false,
        config: defaultConfig(),
        status: 'idle',
        error: null,
        zipName: null,
        version: null,
        loaded: false,
        available: null,
        everMounted: false,
    };
    private onChange: (() => void) | null = null;
    private injector: WidgetInjector | null = null;
    private section: HTMLElement | null = null;
    private observer: MutationObserver | null = null;

    init(onChange: () => void): void {
        this.onChange = onChange;
        this.restoreFromStorage();

        // The zip bytes cannot survive a reload, so nothing is auto-mounted on
        // boot: the tester re-picks the file. The form fields persist, so that
        // is the only thing they have to redo.
        this.state = { ...this.state, enabled: false, status: 'idle', error: null };

        this.onChange();
    }

    destroy(): void {
        this.stopObserving();
        this.teardownInjector();
        this.onChange = null;
    }

    getState(): JackpotWidgetState {
        return {
            ...this.state,
            config: { ...this.state.config, jackpots: [...this.state.config.jackpots] },
        };
    }

    /**
     * Defaults pulled from the live lobby, so QA does not have to retype what
     * the session already knows. Only fills fields the user has not set.
     */
    private withLobbyDefaults(config: JackpotWidgetConfig): JackpotWidgetConfig {
        const store = useAppStore.getState();
        return {
            ...config,
            currency: config.currency || store.balance?.currency || 'EUR',
            language: config.language || (i18next.language || 'EN').toUpperCase(),
            apiHost: config.apiHost || store.settings?.apiHost || '',
        };
    }

    setConfig(patch: Partial<JackpotWidgetConfig>): void {
        this.state = {
            ...this.state,
            config: { ...this.state.config, ...patch },
        };
        this.persist();
        this.onChange?.();
    }

    /**
     * Unpack a picked zip and get its bundle ready to mount. Does NOT mount yet
     * — the tester picks variant/currency/etc. and then hits Apply. Replacing
     * the zip tears down any previous one first.
     */
    async loadZip(name: string, bytes: ArrayBuffer): Promise<void> {
        this.disable();
        this.teardownInjector();

        this.state = {
            ...this.state,
            zipName: name,
            status: 'loading',
            error: null,
            loaded: false,
            version: null,
            available: null,
        };
        this.onChange?.();

        try {
            const { createZipInjector } = await import('./zipInjector');
            const injector = await createZipInjector(bytes);
            await injector.load();

            this.injector = injector;
            this.state = {
                ...this.state,
                status: 'idle',
                loaded: true,
                version: injector.version,
                available: {
                    jackpots: injector.JACKPOTS,
                    types: injector.TYPES,
                    languages: injector.LANGUAGES,
                },
            };
            this.onChange?.();
        } catch (error) {
            this.injector = null;
            this.fail(error instanceof Error ? error.message : String(error));
        }
    }

    /**
     * Mount (or re-mount) the section from the already-loaded zip. Applying with
     * different options re-mounts from scratch — the widgets read their options
     * once, at mount.
     */
    async apply(): Promise<void> {
        if (!this.injector) {
            this.fail('Pick a widget zip first — the .zip you were given, nothing to unpack yet.');
            return;
        }

        const config = this.withLobbyDefaults(this.state.config);
        this.state = { ...this.state, config };

        if (!config.mock && !config.apiHost) {
            this.fail('No stats API. The lobby has no apiHost yet — set one, or tick Mock data.');
            return;
        }

        this.state = { ...this.state, status: 'loading', error: null, enabled: true };
        this.onChange?.();

        try {
            this.mountSection(this.injector, config);
            this.state = { ...this.state, status: 'ready', error: null, everMounted: true };
            this.persist();
            this.onChange?.();
        } catch (error) {
            this.fail(error instanceof Error ? error.message : String(error));
        }
    }

    /** Take the section off the page. See the teardown caveat in the header. */
    disable(): void {
        this.stopObserving();
        this.detachSection();
        this.state = { ...this.state, enabled: false, status: this.state.loaded ? 'idle' : this.state.status, error: null };
        this.persist();
        this.onChange?.();
    }

    private fail(message: string): void {
        this.state = { ...this.state, status: 'error', error: message, enabled: false };
        this.onChange?.();
    }

    /* ── Injector lifecycle ──────────────────────────────────────── */

    private teardownInjector(): void {
        if (this.injector?.destroy) {
            this.injector.destroy();
        }
        this.injector = null;
        this.section = null;
    }

    /* ── Mounting ────────────────────────────────────────────────── */

    /**
     * The widget runs standalone; inside the lobby it inherits the global
     * cascade. The `img { display: block }` reset left-aligns its centre-aligned
     * (inline) level images, and inherited text props leak in — neutralise both
     * at the section boundary. NOT `all: revert` (would force content-box; the
     * widget expects the lobby's border-box) and NOT a shadow root (the widget's
     * `updateResizables` uses document-global `getElementById` /
     * `querySelectorAll`, which do not cross a shadow boundary).
     */
    private ensureEnvFixStyle(): void {
        if (document.getElementById(ENV_FIX_STYLE_ID)) return;
        const style = document.createElement('style');
        style.id = ENV_FIX_STYLE_ID;
        style.textContent = [
            '.egtd-qa-jpw-section {',
            '  line-height: normal; letter-spacing: normal; word-spacing: normal;',
            '  text-transform: none; text-indent: 0; font-style: normal; font-variant: normal;',
            '}',
            '.egtd-qa-jpw-section img { display: inline; }',
        ].join('\n');
        document.head.appendChild(style);
    }

    private mountSection(injector: WidgetInjector, config: JackpotWidgetConfig): void {
        this.stopObserving();
        this.detachSection();
        this.ensureEnvFixStyle();

        const anchor = document.querySelector(SECTION_ANCHOR);
        if (!anchor) {
            throw new Error('No section container on this view. Open the lobby (multi-view) and apply again.');
        }

        this.section = injector.mount({
            jackpots: config.jackpots,
            type: config.type,
            currency: config.currency,
            units: config.units,
            language: config.language,
            apiHost: config.apiHost,
            mock: config.mock,
            title: 'Jackpot widgets (QA)',
            // Wear the same class as a real section so the block inherits the
            // lobby's spacing. The class is a hashed CSS-module name, so it is
            // copied off a live sibling rather than written down anywhere.
            className: anchor.firstElementChild?.className ?? '',
        });

        anchor.insertBefore(this.section, anchor.firstChild);
        this.startObserving(anchor);
    }

    private detachSection(): void {
        // The injector owns the section (and its blob-backed assets); ask it to
        // unmount so it can drop observers too. Fall back to a plain detach.
        if (this.injector) {
            this.injector.unmount();
        } else if (this.section?.parentNode) {
            this.section.parentNode.removeChild(this.section);
        }
        this.section = null;
    }

    /**
     * React owns the section container and rebuilds it on every `setSections`.
     * Re-insert rather than re-mount: the widgets inside are live React roots
     * with open subscriptions, and mounting again would orphan them (see the
     * teardown caveat) and double the widget store's work.
     */
    private startObserving(anchor: Element): void {
        this.observer = new MutationObserver(() => {
            if (!this.state.enabled || !this.section) return;
            if (this.section.isConnected) return;

            const current = document.querySelector(SECTION_ANCHOR);
            if (current) {
                current.insertBefore(this.section, current.firstChild);
            }
        });

        this.observer.observe(anchor, { childList: true });

        // The anchor element itself can be replaced, not just its children -
        // in that case the observer above is watching a detached node, so the
        // body is watched too. Subtree is needed because the container is
        // several levels down from body.
        this.observer.observe(document.body, { childList: true, subtree: true });
    }

    private stopObserving(): void {
        this.observer?.disconnect();
        this.observer = null;
    }

    /* ── Persistence ─────────────────────────────────────────────── */
    // QA sessions expire every few minutes; retyping the form each time is the
    // difference between a tool that gets used and one that does not. The zip
    // bytes are not persisted — the tester re-picks the file after a reload.

    private restoreFromStorage(): void {
        let raw: string | null = null;
        try {
            raw = localStorage.getItem(LS_KEY);
        } catch {
            return;
        }
        if (!raw) return;

        let parsed: PersistedShape | null = null;
        try {
            const candidate = JSON.parse(raw) as unknown;
            if (candidate && typeof candidate === 'object') {
                parsed = candidate as PersistedShape;
            }
        } catch {
            return;
        }
        if (!parsed?.config || typeof parsed.config !== 'object') return;

        // Every field is guarded individually: a preset or a stored blob
        // written by an older build must not be able to poison the form.
        const stored = parsed.config as StoredConfig;
        const config = { ...this.state.config };

        if (Array.isArray(stored.jackpots)) {
            config.jackpots = stored.jackpots.filter((j): j is string => typeof j === 'string');
        }
        if (typeof stored.type === 'string') config.type = stored.type;
        if (typeof stored.currency === 'string') config.currency = stored.currency;
        if (typeof stored.units === 'number' && stored.units > 0) config.units = stored.units;
        if (typeof stored.language === 'string') config.language = stored.language;
        if (typeof stored.mock === 'boolean') config.mock = stored.mock;
        if (typeof stored.apiHost === 'string') config.apiHost = stored.apiHost;

        this.state = { ...this.state, config };
    }

    private persist(): void {
        try {
            localStorage.setItem(LS_KEY, JSON.stringify({ config: this.state.config }));
        } catch {
            // localStorage may be unavailable in sandboxed contexts.
        }
    }
}
